import type { ForumTypeString } from '@/lib/instanceSettings';
import qs from "qs";
import { forumSelect } from "../../forumTypeUtils";
import { siteUrlSetting, allowTypeIIIPlayerSetting } from '@/lib/instanceSettings';
import { combineUrls } from "../../vulcan-lib/utils";
import { TagCommentType } from "../comments/types";
import type { TagLens } from "@/lib/arbital/useTagLenses";
import { getSortOrderOptions, SettingsOption } from "../posts/dropdownOptions";
import type { TagHistorySettings } from "@/components/tagging/history/TagHistoryPage";

export const getTagMinimumKarmaPermissions = (forumType: ForumTypeString) => forumSelect({
  // Topic spampocalypse defense
  EAForum: {
    new: 1,
    edit: 1,
  },
  LessWrong: {
    new: 1,
    edit: 1,
  },
  // Default is to allow all users to create/edit tags
  default: {
    new: -1000,
    edit: -1000,
  }
}, forumType)

interface GetUrlOptions {
  edit?: boolean,
  flagId?: string
  lens?: string
  tab?: string
  from?: string,
  pathId?: string
}

export const getTagCreateUrl = () => `/w/create`
export const getTagGradingSchemeUrl = () => `/w/tag-grading-scheme`

// Relative URLs do not depend on the forum's domain.
export const tagGetUrl = (tag: {slug: string}, urlOptions?: GetUrlOptions, hash?: string) => {
  const urlSearchParams = urlOptions
  const search = qs.stringify(urlSearchParams)

  const searchSuffix = search ? `?${search}` : ''
  const hashSuffix = hash ? `#${hash}` : ''

  const url = `/w/${tag.slug}`
  const urlWithSuffixes = `${url}${searchSuffix}${hashSuffix}`
  return urlWithSuffixes
}

export const tagGetAbsoluteUrl = (tag: {slug: string}, forumType: ForumTypeString, urlOptions?: GetUrlOptions, hash?: string) => {
  return combineUrls(siteUrlSetting.get(forumType), tagGetUrl(tag, urlOptions, hash));
}

export const tagGetHistoryUrl = (tag: {slug: string}) => `/w/${tag.slug}/history`

export const tagGetDiscussionUrl = (tag: {slug: string}) => `/w/${tag.slug}/discussion`

export const tagGetAbsoluteDiscussionUrl = (tag: {slug: string}, forumType: ForumTypeString) => {
  return combineUrls(siteUrlSetting.get(forumType), tagGetDiscussionUrl(tag));
}

export const tagGetSubforumUrl = (tag: {slug: string}) => tagGetUrl(tag, {tab: "posts"})

export const tagGetAbsoluteSubforumUrl = (tag: {slug: string}, forumType: ForumTypeString) => {
  return combineUrls(siteUrlSetting.get(forumType), tagGetSubforumUrl(tag));
}

interface TagCommentLinkOptions {
  tagSlug: string,
  commentId?: string | null,
  tagCommentType: TagCommentType,
}

export const tagGetCommentLink = ({tagSlug, commentId, tagCommentType = "DISCUSSION"}: TagCommentLinkOptions): string => {
  const base = tagCommentType === "DISCUSSION" ? tagGetDiscussionUrl({slug: tagSlug}) : tagGetSubforumUrl({slug: tagSlug})

  // Bit of a hack to make it work whether or not there are already query params, if this breaks just parse the URL properly
  return commentId ? `${base}${base.includes('?') ? "&" : "?"}commentId=${commentId}` : base
}

export const tagGetAbsoluteCommentLink = (options: TagCommentLinkOptions, forumType: ForumTypeString): string => {
  return combineUrls(siteUrlSetting.get(forumType), tagGetCommentLink(options));
}

// TODO: Is this necessary if we instead have version as a search param in the main tagGetUrl function?
export const tagGetRevisionLink = (tag: DbTag|TagBasicInfo, versionNumber: string, lens?: MultiDocumentContentDisplay|TagLens): string => {
  const lensParam = lens ? `lens=${lens.slug}&` : "";
  return `/w/${tag.slug}?${lensParam}version=${versionNumber}`;
}

export const tagUserHasSufficientKarma = (user: UsersCurrent | DbUser | null, action: "new" | "edit", forumType: ForumTypeString): boolean => {
  if (!user) return false
  if (user.isAdmin) return true
  if ((user.karma) >= getTagMinimumKarmaPermissions(forumType)[action]) return true
  return false
}

const MINIMUM_TAG_RENAME_KARMA = 1;

export const userCanRenameTag = (user: UsersCurrent | DbUser | null): boolean => {
  if (!user) return false;
  return user.karma >= MINIMUM_TAG_RENAME_KARMA;
}

export const userCanModerateSubforum = (user: UsersCurrent | DbUser | null, tag: { subforumModeratorIds: string[] }) => {
  if (!user) return false
  if (user.isAdmin || user?.groups?.includes("sunshineRegiment")) return true
  if (tag.subforumModeratorIds?.includes(user._id)) return true
  return false
}

export const userIsSubforumModerator = (user: DbUser|UsersCurrent|null, tag: Pick<DbTag, "subforumModeratorIds">): boolean => {
  if (!user || !tag) return false;
  return tag.subforumModeratorIds?.includes(user._id);
}

/**
 * Sort tags in order of: core-ness, score, name (alphabetical). If we don't have the scores, sort only by core-ness.
 */
export function stableSortTags<
  T extends {name: string; core: boolean},
  TR extends {baseScore: number} | null | undefined
>(
  tagInfo: Array<{ tag: T; tagRel: TR }>,
  options?: { coreTags?: "first"|"last" },
): Array<{ tag: T; tagRel: TR }> {
  const coreTagsSort = (options?.coreTags === "first" ? 1 : -1);

  return [...tagInfo].sort((a, b) => {
    const tagA = a.tag;
    const tagB = b.tag;
    const tagRelA = a.tagRel;
    const tagRelB = b.tagRel;

    if (tagA.core !== tagB.core) {
      // Core tags come first unless options?.coreTags is "last"
      return (tagA.core ? -1 : 1) * coreTagsSort;
    }

    if (tagRelA && tagRelB) {
      if (tagRelA.baseScore !== tagRelB.baseScore) {
        return (tagRelB.baseScore || 0) - (tagRelA.baseScore || 0);
      }

      return tagA.name.localeCompare(tagB.name);
    }

    return 0;
  });
}

export const isTagAllowedType3Audio = (tag: TagPageFragment|DbTag, forumType: ForumTypeString): boolean => {
  if (!allowTypeIIIPlayerSetting.get(forumType)) return false

  return !!tag.forceAllowType3Audio && !!tag.description && !tag.deleted
};

export const getTagPostsSortOrderOptions = () => ({
  relevance: { label: "Most Relevant" },
  ...getSortOrderOptions(),
} satisfies Record<string, SettingsOption>);

export const defaultTagHistorySettings: TagHistorySettings = {
  //displayFormat: "dense",
  displayFormat: "expanded",
  showEdits: true,
  showSummaryEdits: true,
  showComments: true,
  showTagging: true,
  showMetadata: true,
  lensId: "all",
};
